import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandS } from '@sorare/core/src/atoms/typography';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_REWARDS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import {
  getFixtureQueryVariables,
  getSo5LeaguesByGroup,
  groupTitles,
} from 'lib/so5';

import { LivePastLeaderboard } from './LivePastLeaderboard';
import type {
  NewFixtureRewardsQuery,
  NewFixtureRewardsQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_REWARDS_QUERY = gql`
  query NewFixtureRewardsQuery($slug: String, $type: So5State, $sport: Sport!) {
    so5 {
      so5Fixture(slug: $slug, type: $type, sport: $sport) {
        slug
        so5LeaderboardGroups(groupType: COMPETITION) {
          slug
          so5LeagueTrackGroups {
            slug
            so5LeagueTracks {
              slug
            }
          }
          ...LivePastLeaderboard_so5LeaderboardGroup
        }
        ...getSo5LeaguesByGroup_so5Fixture
      }
    }
  }
  ${getSo5LeaguesByGroup.fragments.so5Fixture}
  ${LivePastLeaderboard.fragments.so5LeaderboardGroup}
` as TypedDocumentNode<NewFixtureRewardsQuery, NewFixtureRewardsQueryVariables>;

const Root = styled(Vertical)`
  padding-top: var(--double-unit);
`;

const Grid = styled.div`
  --items-per-row: 1;
  display: grid;
  grid-template-columns: repeat(var(--items-per-row), 1fr);
  gap: var(--double-unit);

  @media ${tabletAndAbove} {
    --items-per-row: 2;
  }

  @media ${desktopAndAbove} {
    --items-per-row: 3;
  }
`;

type Props = {
  fixture: string;
  isUpcoming: boolean;
  sport: Sport;
  composableOnly?: boolean;
};

export const NewFixtureRewards = ({
  fixture,
  isUpcoming,
  sport,
  composableOnly = true,
}: Props) => {
  const openDialog = useOpenDialog();
  const { generateSportContextPath } = useSportContext();
  const { data, loading } = useQuery(FIXTURE_REWARDS_QUERY, {
    variables: {
      ...getFixtureQueryVariables(fixture),
      sport,
    },
  });

  const { so5Fixture } = data?.so5 || {};

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!so5Fixture) {
    return null;
  }

  const groupedSo5Leagues = isUpcoming
    ? getSo5LeaguesByGroup(so5Fixture, composableOnly)
    : getSo5LeaguesByGroup(so5Fixture, false);

  return (
    <FullWidthContainer>
      <Root gap={4}>
        {Object.entries(groupedSo5Leagues).map(([key, value]) => {
          if (value.length === 0) {
            return null;
          }
          return (
            <Vertical gap={2} key={key}>
              <BrandS>
                <FormattedMessage
                  {...groupTitles[key as keyof typeof groupedSo5Leagues]}
                />
              </BrandS>
              <Grid
                style={
                  {
                    '--hover-opacity': 0.5,
                    '--custom-padding':
                      'calc(6 * var(--unit)) var(--double-unit) calc(3 * var(--unit)) var(--double-unit)',
                  } as CSSProperties
                }
              >
                {value.map(so5LeaderboardGroup => (
                  <LivePastLeaderboard
                    so5LeaderboardGroup={so5LeaderboardGroup}
                    key={so5LeaderboardGroup.slug}
                    {...(isUpcoming
                      ? {
                          onClick: () => {
                            openDialog(
                              DialogKey.competitionDrawer,
                              so5LeaderboardGroup.slug,
                              {
                                additionalParams: {
                                  [DialogAdditionalParamKey.trck]:
                                    so5LeaderboardGroup.so5LeagueTrackGroups[0]
                                      .so5LeagueTracks[0]?.slug,
                                  [DialogAdditionalParamKey.trckGrp]:
                                    so5LeaderboardGroup.so5LeagueTrackGroups[0]
                                      .slug,
                                },
                              }
                            );
                          },
                        }
                      : {
                          to: generateSportContextPath(
                            ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_REWARDS,
                            {
                              params: {
                                fixture: so5Fixture.slug,
                                competition: so5LeaderboardGroup.slug,
                                eventType: so5Fixture.type.toLowerCase(),
                              },
                            }
                          ),
                        })}
                  />
                ))}
              </Grid>
            </Vertical>
          );
        })}
      </Root>
    </FullWidthContainer>
  );
};
